/**
 * InsufficientCreditsModal — generic "add credits to continue" dialog.
 *
 * Extracted from the inline popup that lived inside AppsMarketplaceSection
 * so both Apps (deploy) and Workflows (enable warm pool) can reuse a single
 * visual + behavioural pattern when the backend returns a 402.
 *
 * Wraps the existing `ConfirmModal` for visual consistency — does NOT
 * fork or restyle it. Only swaps the body content from app-specific
 * hard-coded copy to a small template parameterised by the caller.
 *
 * Body copy template (computed from props):
 *
 *   You're trying to {actionLabel}.
 *
 *   [If rateLabel + minRuntimeLabel]
 *     This requires at least <strong>{minRuntimeLabel}</strong> of runtime
 *     at <strong>{rateLabel}</strong>.
 *   [Else if rateLabel only]
 *     This costs <strong>{rateLabel}</strong>.
 *   [Else if minRuntimeLabel only]
 *     This requires at least <strong>{minRuntimeLabel}</strong> of runway.
 *
 *   [If balance or min available]
 *     Your balance is <strong>{balanceStr}</strong> (minimum <strong>{minStr}</strong>).
 *
 *   Add credits on the [billing page](/billing) to continue.
 */
import React from 'react';

import { formatBalance } from '../../services/credits';
import ConfirmModal, { ConfirmModalLink } from '../molecules/ConfirmModal/ConfirmModal';

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} [props.title]                    default "Add credits to continue"
 * @param {string} [props.ariaLabel]                aria-label for the dialog
 * @param {string} props.actionLabel                what the user was trying to do
 * @param {string} [props.rateLabel]                e.g. "$0.25/hr"
 * @param {string} [props.minRuntimeLabel]          e.g. "5 minutes" / "1 day"
 * @param {number} [props.minRequiredMilliCents]    for "(minimum X)" line
 * @param {number|null} [props.balanceMilliCents]   current balance; null = unknown
 * @param {string} [props.primaryLabel]             default "Go to Billing"
 * @param {string} [props.primaryTo]                default "/billing"
 * @param {string} [props.secondaryLabel]           default "Cancel"
 * @param {() => void} [props.onPrimary]            extra side-effect on primary click
 */
export default function InsufficientCreditsModal({
  open,
  onClose,
  title = 'Add credits to continue',
  ariaLabel,
  actionLabel,
  rateLabel,
  minRuntimeLabel,
  minRequiredMilliCents,
  balanceMilliCents = null,
  primaryLabel = 'Go to Billing',
  primaryTo = '/billing',
  secondaryLabel = 'Cancel',
  onPrimary,
}) {
  // Format helpers — `formatBalance` already handles missing / non-finite
  // values; only call it when the prop was actually provided so we can
  // render an em-dash for "unknown" rather than the literal "$0.00".
  const balanceStr = balanceMilliCents !== null && balanceMilliCents !== undefined
    ? formatBalance(balanceMilliCents, { precision: 'auto' })
    : '—';
  const hasMin = minRequiredMilliCents !== null
    && minRequiredMilliCents !== undefined
    && Number.isFinite(Number(minRequiredMilliCents));
  const minStr = hasMin
    ? formatBalance(minRequiredMilliCents, { precision: 'auto' })
    : '—';

  // Pick the right "this costs X" sentence based on which cost props the
  // caller passed. Keep the if/else explicit — three small branches read
  // more clearly than a chained ternary at the JSX site.
  let costSentence = null;
  if (rateLabel && minRuntimeLabel) {
    costSentence = (
      <>
        This requires at least <strong style={{ color: '#fff' }}>{minRuntimeLabel}</strong>
        {' '}of runtime at <strong style={{ color: '#fff' }}>{rateLabel}</strong>.
      </>
    );
  } else if (rateLabel) {
    costSentence = (
      <>This costs <strong style={{ color: '#fff' }}>{rateLabel}</strong>.</>
    );
  } else if (minRuntimeLabel) {
    costSentence = (
      <>
        This requires at least <strong style={{ color: '#fff' }}>{minRuntimeLabel}</strong>
        {' '}of runway.
      </>
    );
  }

  // Only render the "Your balance is X (minimum Y)" line when at least
  // one of the two numbers is actually known. Avoids a useless "— (minimum —)"
  // sentence when the caller doesn't have either.
  const showBalanceLine = (balanceMilliCents !== null && balanceMilliCents !== undefined) || hasMin;

  return (
    <ConfirmModal
      open={open}
      onClose={onClose}
      title={title}
      ariaLabel={ariaLabel || title}
      primaryLabel={primaryLabel}
      primaryTo={primaryTo}
      onPrimary={onPrimary}
      secondaryLabel={secondaryLabel}
    >
      <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, marginBottom: 20 }}>
        {actionLabel ? (
          <>You&apos;re trying to <strong style={{ color: '#fff' }}>{actionLabel}</strong>.{' '}</>
        ) : null}
        {costSentence}
        {showBalanceLine ? (
          <>
            {' '}Your balance is <strong style={{ color: '#fff' }}>{balanceStr}</strong>
            {' '}(minimum <strong style={{ color: '#fff' }}>{minStr}</strong>).
          </>
        ) : null}
        {' '}Add credits on the{' '}
        <ConfirmModalLink
          to={primaryTo}
          onClick={() => {
            onPrimary?.();
            onClose?.();
          }}
        >
          billing page
        </ConfirmModalLink>
        {' '}to continue.
      </p>
    </ConfirmModal>
  );
}
